import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWaysteClient } from '@alliance-disposal/client';
import { Equipment } from '@alliance-disposal/transport-types';
import { EquipmentPill } from '@wayste/sour-ui';
import { formatServiceAddress, getContainerTypeName } from '@wayste/utils';
import mapboxgl from 'mapbox-gl';
import { createRoot } from 'react-dom/client';
import { PiCubeFill, PiHouseFill, PiTrashFill } from 'react-icons/pi';
import { useMap } from '../../hooks/useMap';
import MapPopup from './components/MapPopup';
import PinMapSettings from './components/PinMapSettings';
import { fitBounds, iconConverter } from './utils';
export const PinMap = ({ markerSets, center, zoom, style, selectedMarker, selectedMarkerSet, onDoubleClick, onMarkerClick, showRecenter, visibleSettings, visibleExtraLayers, }) => {
    const client = useWaysteClient();
    const defaultLineOpacity = 0.3;
    const isMapping = useRef(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isCentered, setIsCentered] = useState(false);
    // Grab settings from local storage if they exist, otherwise set defaults
    const [settings, setSettings] = useState(() => {
        if (visibleSettings?.length === 0)
            return null;
        const defaultSettings = {
            autoCenter: true,
            clustering: true,
            markerLabels: true,
            lines: false,
        };
        const savedSettings = localStorage.getItem('pin-map-settings');
        const parsedSettings = savedSettings ? JSON.parse(savedSettings) : {};
        // Merge default settings with saved settings
        const mergedSettings = { ...defaultSettings, ...parsedSettings };
        // Remove settings not included in visibleSettings
        if (visibleSettings !== undefined) {
            Object.keys(mergedSettings).forEach((key) => {
                if (!visibleSettings.includes(key)) {
                    delete mergedSettings[key];
                }
            });
        }
        return mergedSettings;
    });
    // Grab layers from local storage if they exist, otherwise set defaults
    const [layers, setLayers] = useState(() => {
        if (visibleExtraLayers?.length === 0)
            return null;
        const defaultLayers = {
            disposalSites: true,
            yards: true,
            containers: true,
        };
        const savedLayers = localStorage.getItem('pin-map-layers');
        const parsedLayers = savedLayers ? JSON.parse(savedLayers) : {};
        // Merge default settings with saved layers
        const mergedLayers = { ...defaultLayers, ...parsedLayers };
        // Remove layers not included in visibleExtraLayers
        if (visibleExtraLayers !== undefined) {
            Object.keys(mergedLayers).forEach((key) => {
                if (!visibleExtraLayers.includes(key)) {
                    delete mergedLayers[key];
                }
            });
        }
        return mergedLayers;
    });
    const mapEventHandlersRef = useRef();
    const eventHandlersRef = useRef({});
    const extraLayerEventHandlersRef = useRef({});
    const prevMarkerSetsRef = useRef([]);
    const popupRef = useRef(new mapboxgl.Popup({
        offset: 20,
        closeButton: false,
        closeOnClick: false,
        // anchor: 'right',
    }));
    const { map, mapRef } = useMap({
        onLoad: () => {
            setIsMapLoaded(true);
        },
        center,
        zoom,
        style,
    });
    /**
     * Adds the appropriate icon images to the map for the given marker set.
     *
     * @param map - The mapboxgl.Map instance.
     * @param markerSet - The marker set containing the icons and markers.
     * @returns A Promise that resolves when all icon images have been added to the map.
     * @throws If there is an error adding the icon images.
     */
    const addIconImages = async (map, markerSet) => {
        try {
            if (map.hasImage(`markerSetIcon-${markerSet.id}`)) {
                map.removeImage(`markerSetIcon-${markerSet.id}`);
            }
            const markerSetIcon = await iconConverter({ icon: markerSet.icon, color: markerSet.color, size: 50 });
            map.addImage(`markerSetIcon-${markerSet.id}`, markerSetIcon);
            await Promise.all(markerSet.markers.map(async (marker) => {
                if (!marker.overrideIcon && !marker.overrideColor)
                    return;
                const overrideIcon = marker.overrideIcon || markerSet.icon;
                const overrideColor = marker.overrideColor || markerSet.color;
                const image = await iconConverter({ icon: overrideIcon, color: overrideColor, size: 50 });
                if (map.hasImage(`markerIcon-${marker.id}`)) {
                    map.removeImage(`markerIcon-${marker.id}`);
                }
                map.addImage(`markerIcon-${marker.id}`, image);
            }));
        }
        catch (error) {
            console.error('Error adding icon images:', error);
        }
    };
    /**
     * Initializes all the layers as well as the event listeners from markerSets, called each time markerSets changes
     *
     * @param map - The mapboxgl.Map instance.
     */
    const initLayersAndListeners = async (map) => {
        //setup all layers
        if (isMapping.current) {
            console.warn('Already mapping, skipping');
            return;
        }
        try {
            isMapping.current = true;
            await initLayers(map);
        }
        catch (error) {
            console.error('Error initializing layers:', error);
        }
        finally {
            isMapping.current = false;
        }
        // Clean up old map event listeners
        if (mapEventHandlersRef?.current)
            removeMapEventListeners(map, mapEventHandlersRef.current);
        // Add the new ones
        const newMapEventHandlers = addMapEventListeners(map);
        mapEventHandlersRef.current = newMapEventHandlers;
        // Apply relevant properties to each marker set
        const newEventHandlers = {};
        markerSets.forEach((markerSet) => {
            // Add event listeners for visible marker sets
            if (markerSet.isVisible) {
                const markerLayerID = `route-${markerSet.id}`;
                newEventHandlers[markerLayerID] = addEventListeners(map, markerSet)[markerLayerID];
            }
            // Update line layer styles depending on selected marker set
            if (markerSet.isConnected && settings?.lines && markerSet.markers.length > 1) {
                const lineLayerID = `route-${markerSet.id}-line`;
                if (selectedMarkerSet && markerSet.id === selectedMarkerSet.id) {
                    map.setPaintProperty(lineLayerID, 'line-opacity', 1);
                    map.setPaintProperty(lineLayerID, 'line-width', 2);
                }
                else {
                    map.setPaintProperty(lineLayerID, 'line-opacity', markerSet.lineOpacity || defaultLineOpacity);
                    map.setPaintProperty(lineLayerID, 'line-width', 1);
                }
            }
        });
        // Clean up old event listeners
        Object.entries(eventHandlersRef.current).forEach(([markerLayerID, handlers]) => {
            const markerSet = markerSets.find((set) => `route-${set.id}` === markerLayerID);
            if (markerSet) {
                removeEventListeners(map, markerSet, { [markerLayerID]: handlers });
            }
        });
        // Update the event handlers ref
        eventHandlersRef.current = newEventHandlers;
        if (selectedMarkerSet && settings?.autoCenter) {
            fitBounds(map, [selectedMarkerSet]);
            setIsCentered(true);
        }
        // If a marker is selected go to it, note that this will override fitting for the marker set
        if (selectedMarker && settings?.autoCenter) {
            map.easeTo({ center: selectedMarker.lngLat, zoom: 15 });
            setIsCentered(false);
        }
    };
    /**
     * Initializes the markerSets layers on the map
     *
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @returns A Promise that resolves when the layers and sources are initialized.
     */
    const initLayers = async (map) => {
        if (!map || !markerSets) {
            console.error("Map or marker sets don't exist");
            return;
        }
        // Remove any straggling popups
        popupRef.current?.remove();
        // Remove old layers and sources
        const prevMarkerSets = prevMarkerSetsRef.current;
        if (prevMarkerSets) {
            prevMarkerSets.forEach((markerSet) => {
                const lineLayerID = `route-${markerSet.id}-line`;
                if (map.getLayer(lineLayerID)) {
                    map.removeLayer(lineLayerID);
                    map.removeSource(lineLayerID);
                }
                const markerLayerID = `route-${markerSet.id}`;
                if (map.getLayer(`${markerLayerID}-label`)) {
                    map.removeLayer(`${markerLayerID}-label`);
                }
                if (map.getLayer(`${markerLayerID}-clusters`)) {
                    map.removeLayer(`${markerLayerID}-clusters`);
                }
                if (map.getLayer(`${markerLayerID}-cluster-count`)) {
                    map.removeLayer(`${markerLayerID}-cluster-count`);
                }
                if (map.getLayer(markerLayerID)) {
                    map.removeLayer(markerLayerID);
                }
                if (map.getSource(markerSet.id)) {
                    map.removeSource(markerSet.id);
                }
            });
        }
        // Update the previous marker sets ref to the current marker sets
        prevMarkerSetsRef.current = markerSets;
        // Add new layers and sources if visible
        const visibleMarkerSets = markerSets
            .filter((set) => set.isVisible)
            .sort((a, b) => {
            if (selectedMarkerSet) {
                if (a.id === selectedMarkerSet.id)
                    return 1;
                if (b.id === selectedMarkerSet.id)
                    return -1;
            }
            return 0;
        });
        await Promise.all(visibleMarkerSets.map(async (markerSet) => {
            await addAllLayers(map, markerSet);
        }));
        // If no settings are provided, recenter when a new markerSet is given so that the bounds are fit
        if (map && visibleMarkerSets && (settings?.autoCenter ?? true)) {
            fitBounds(map, visibleMarkerSets);
            setIsCentered(true);
        }
    };
    /**
     * Adds all layers to the map for a given marker set.
     *
     * @param map - The mapboxgl.Map instance.
     * @param markerSet - The marker set containing the markers to be added.
     * @returns A Promise that resolves when all layers have been added.
     */
    const addAllLayers = async (map, markerSet) => {
        const markerLayerID = `route-${markerSet.id}`;
        if (map.getLayer(markerLayerID)) {
            console.error(`Marker layer ${markerLayerID} exists`);
            return;
        }
        if (map.getSource(markerSet.id)) {
            console.error(`Source ${markerSet.id} exists`);
            return;
        }
        await addIconImages(map, markerSet);
        // Adds the source (the markers) to the map, which is then used to create the various layers
        map.addSource(markerSet.id, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: markerSet.markers.map((markerData) => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: markerData.lngLat,
                    },
                    properties: {
                        id: markerData.id,
                        title: markerData.title,
                        label: markerData.label,
                        sortKey: markerData.id === selectedMarker?.id ? 1 : 0, // ensures that the selected marker is always shown on top
                        icon: markerData.overrideIcon || markerData.overrideColor
                            ? `markerIcon-${markerData.id}`
                            : `markerSetIcon-${markerSet.id}`,
                    },
                })),
            },
            cluster: settings?.clustering || false, //turn clustering on/off with settings, adds point_count property to features
            clusterMaxZoom: 14, // Max zoom for clustering points
            clusterRadius: 30, /// Radius of each cluster when clustering points
            clusterMinPoints: 5, // prevents clustering below 5 points
        });
        if (markerSet.isConnected && settings?.lines && markerSet.markers.length > 1) {
            await addLineLayer(map, markerSet);
        }
        if (settings?.clustering) {
            // Adds the clustering circles
            map.addLayer({
                id: `${markerLayerID}-clusters`,
                type: 'circle',
                source: markerSet.id,
                filter: ['has', 'point_count'], // gets just the point_count property from the source
                paint: {
                    'circle-color': markerSet.color, // same color no matter cluster size, can also customize similar to below
                    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 25, 50], // 20px circles when <10 points, 30px at 25 points, 50px at >25 points, lower limit of 5 points set by clusterMinPoints above
                },
            });
            // Adds the clustering labels
            map.addLayer({
                id: `${markerLayerID}-cluster-count`,
                type: 'symbol',
                source: markerSet.id,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                },
            });
        }
        // Adds the markers to the map
        map.addLayer({
            id: markerLayerID,
            type: 'symbol',
            source: markerSet.id,
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': ['get', 'icon'], // Use the 'icon' property of each feature to determine the image
                'icon-size': 0.5, // Images are purposefully rasterized as very large to preserve detail, so we scale them down
                'icon-allow-overlap': true, // Removes effect where markers go invisible when zoomed out
                'symbol-sort-key': ['get', 'sortKey'], // Use the sortKey property from your GeoJSON features to sort the icons
            },
        });
        // Adds the marker labels which is controlled by the settings
        if (settings?.markerLabels) {
            map.addLayer({
                id: `${markerLayerID}-label`,
                type: 'symbol',
                source: markerSet.id, // Use the same source as your markers
                layout: {
                    'text-field': ['get', 'label'], // Use the label property from each feature
                    'text-size': 12, // Customize text size as needed
                    'text-anchor': 'center',
                    'text-offset': [0, -1.5], // Offset text slightly for better visibility
                },
                paint: {
                    'text-color': '#000000', // Sets text color to black
                    'text-halo-color': '#FFFFFF', // Sets halo color to white
                    'text-halo-width': 2, // Adjust halo width for visibility
                },
            });
        }
    };
    /**
     * Helper function to add the line layer to the map per marker set.
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MarkerSet} markerSet - The marker set containing the markers.
     * @returns A Promise that resolves when the line layer has been added.
     */
    const addLineLayer = async (map, markerSet) => {
        const markers = markerSet.markers;
        if (markers.length < 2) {
            return; // Not enough markers to draw a line
        }
        const lineLayerID = `route-${markerSet.id}-line`;
        if (map.getLayer(lineLayerID)) {
            console.error(`Line layer ${lineLayerID} exists`);
            return;
        }
        // Adds the line layer
        map.addLayer({
            id: lineLayerID,
            type: 'line',
            source: {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {}, // You can add properties for styling, if needed
                    geometry: {
                        type: 'LineString',
                        coordinates: markers.map((marker) => marker.lngLat),
                    },
                },
            },
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-color': markerSet.lineColor || markerSet.color,
                'line-width': 2,
                'line-opacity': markerSet.lineOpacity || defaultLineOpacity,
            },
        });
    };
    /**
     * Helper function to add event listeners to the map per marker set and returns the event handlers.
     *
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MarkerSet} markerSet - The marker set containing the markers.
     * @returns {MarkerSetEventHandlers} The event handlers for the marker set.
     */
    const addEventListeners = useCallback((map, markerSet) => {
        const markerLayerID = `route-${markerSet.id}`;
        const eventHandlers = {
            [markerLayerID]: {
                clusterMouseEnter: () => {
                    map.getCanvas().style.cursor = 'pointer';
                },
                clusterMouseLeave: () => {
                    if (!onDoubleClick)
                        map.getCanvas().style.cursor = '';
                },
                clusterClick: (e) => {
                    const features = map.queryRenderedFeatures(e.point, {
                        layers: [`${markerLayerID}-clusters`],
                    });
                    const clusterId = features[0].id;
                    map.getSource(markerSet.id).getClusterExpansionZoom(clusterId, (err, zoom) => {
                        if (err)
                            return;
                        map.easeTo({
                            center: features[0].geometry.coordinates,
                            zoom: zoom,
                        });
                    });
                },
                markerClick: (e) => {
                    const features = map.queryRenderedFeatures(e.point);
                    const markerID = features?.[0]?.properties?.id;
                    const markerData = markerSet.markers.find((marker) => marker.id === markerID);
                    if (onMarkerClick && markerData)
                        onMarkerClick(markerData);
                },
                markerMouseEnter: (e) => {
                    map.getCanvas().style.cursor = 'pointer';
                    const features = map.queryRenderedFeatures(e.point);
                    const markerID = features?.[0]?.properties?.id;
                    const markerData = markerSet.markers.find((marker) => marker.id === markerID);
                    if (!markerData)
                        return;
                    const popupNode = document.createElement('div');
                    createRoot(popupNode).render(_jsx(MapPopup, { title: markerData.title, content: markerData.content }));
                    popupRef.current?.setLngLat(e.lngLat).setDOMContent(popupNode).addTo(map);
                },
                markerMouseLeave: () => {
                    if (!onDoubleClick)
                        map.getCanvas().style.cursor = '';
                    popupRef.current?.remove();
                },
            },
        };
        map.on('mouseenter', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterMouseEnter);
        map.on('mouseleave', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterMouseLeave);
        map.on('click', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterClick);
        map.on('click', markerLayerID, eventHandlers[markerLayerID].markerClick);
        map.on('mouseenter', markerLayerID, eventHandlers[markerLayerID].markerMouseEnter);
        map.on('mouseleave', markerLayerID, eventHandlers[markerLayerID].markerMouseLeave);
        return eventHandlers;
    }, [onMarkerClick]);
    /**
     * Helper function to remove event listeners per marker set
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MarkerSet} markerSet - The marker set containing the markers.
     * @param {MarkerSetEventHandlers} eventHandlers - The event handlers for the marker set.
     */
    const removeEventListeners = useCallback((map, markerSet, eventHandlers) => {
        const markerLayerID = `route-${markerSet.id}`;
        map.off('mouseenter', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterMouseEnter);
        map.off('mouseleave', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterMouseLeave);
        map.off('click', `${markerLayerID}-clusters`, eventHandlers[markerLayerID].clusterClick);
        map.off('click', markerLayerID, eventHandlers[markerLayerID].markerClick);
        map.off('mouseenter', markerLayerID, eventHandlers[markerLayerID].markerMouseEnter);
        map.off('mouseleave', markerLayerID, eventHandlers[markerLayerID].markerMouseLeave);
    }, []);
    /**
     * Sets up the disposal site marker set.
     * Retrieves disposal sites from the server and creates marker data for each site.
     *
     * @returns A Promise that resolves to a MarkerSet object representing the 'disposal-sites' marker set.
     */
    const setupDisposalSiteMarkerSet = async () => {
        const disposalSites = await client.disposalSite().wayste.query();
        const disposalSiteMarkers = disposalSites.map((site) => ({
            id: site.id,
            title: site.name,
            content: formatServiceAddress(site.address),
            lngLat: [site.coordinates.lng, site.coordinates.lat],
        }));
        const disposalSiteMarkerSet = {
            id: 'disposal-sites',
            color: '#307351',
            icon: _jsx(PiTrashFill, { className: "size-5" }),
            isVisible: true,
            name: 'Disposal Sites',
            markers: disposalSiteMarkers,
            isConnected: false,
        };
        return disposalSiteMarkerSet;
    };
    /**
     * Sets up the yard marker set
     * Retrieves yards from the server and creates marker data for each yard.
     *
     * @returns A Promise that resolves to a MarkerSet object representing the 'yards' marker set.
     */
    const setupYardMarkerSet = async () => {
        const yards = await client.hauler().yard.query();
        const yardMarkers = yards.results.map((yard) => ({
            id: yard.id,
            title: yard.name || 'Yard',
            content: formatServiceAddress(yard.yardAddress),
            lngLat: [yard.longitude || 0, yard.latitude || 0],
        }));
        const yardMarkerSet = {
            id: 'yards',
            color: '#92140C',
            icon: _jsx(PiHouseFill, { className: "size-5" }),
            isVisible: true,
            name: 'Yards',
            markers: yardMarkers,
            isConnected: false,
        };
        return yardMarkerSet;
    };
    /**
     * Sets up the container marker set.
     * Retrieves containers from the server and creates marker data for each container.
     * Filters out containers with location class 'YARD'.
     *
     * @returns A Promise that resolves to a MarkerSet object representing the 'containers' marker set.
     */
    const setupContainerMarkerSet = async () => {
        const containers = await client.equipment().wayste.taggedContainer.query({ limit: 200 });
        const containerMarkers = containers.results
            .filter((container) => container.lastActivity && container.lastActivity.locationClass !== 'YARD')
            .map((container) => ({
            id: container.id,
            title: `Container ${container.name}`,
            content: (_jsxs("div", { className: "grid grid-cols-[auto_1fr] gap-1", children: [_jsxs("div", { className: "col-span-full grid grid-cols-subgrid items-center gap-2", children: [_jsx("div", { className: "text-right font-bold", children: "Status:" }), _jsx(EquipmentPill, { type: "CONTAINER", status: container.status })] }), _jsxs("div", { className: "col-span-full grid grid-cols-subgrid items-center gap-2", children: [_jsx("div", { className: "text-right font-bold", children: "Type:" }), _jsx("div", { children: getContainerTypeName(container.containerType) })] }), _jsx("div", { className: "text-right font-bold", children: "Last Location:" }), _jsx("div", { children: Equipment.LocationClassLabels[container.lastActivity.locationClass] }), _jsxs("div", { className: "col-span-full grid grid-cols-subgrid items-center gap-2", children: [_jsx("div", { className: "text-right font-bold", children: "Address:" }), _jsx("div", { children: formatServiceAddress(container.lastActivity.address) })] })] })),
            lngLat: [container.lastActivity?.coordinates?.longitude || 0, container.lastActivity?.coordinates?.latitude || 0],
        }));
        const containerMarkerSet = {
            id: 'containers',
            color: '#A97C73',
            icon: _jsx(PiCubeFill, { className: "size-5" }),
            isVisible: true,
            name: 'Containers',
            markers: containerMarkers,
            isConnected: false,
        };
        return containerMarkerSet;
    };
    /**
     * Initializes the extra layers (disposal sites, yards, containers) as controlled by the layers control on the map.
     *
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @returns A Promise that resolves when the extra layers are initialized.
     */
    const initExtraLayers = async (map) => {
        if (!map || !layers) {
            console.error("Map or layers don't exist");
            return;
        }
        try {
            const extraLayers = [];
            // Handles adding/removing the disposal sites layer based on settings
            if (layers.disposalSites && !map.getLayer('disposal-sites')) {
                if (map.getSource('disposal-sites')) {
                    await addExtraLayer(map, 'disposal-sites');
                }
                else {
                    const disposalSiteMarkerSet = await setupDisposalSiteMarkerSet();
                    await addAllExtraLayers(map, disposalSiteMarkerSet);
                    extraLayers.push(disposalSiteMarkerSet);
                }
            }
            else if (!layers.disposalSites && map.getLayer('disposal-sites')) {
                map.removeLayer('disposal-sites');
            }
            // Handles adding/removing the yards layer based on settings
            if (layers.yards && !map.getLayer('yards')) {
                if (map.getSource('yards')) {
                    await addExtraLayer(map, 'yards');
                }
                else {
                    const yardMarkerSet = await setupYardMarkerSet();
                    await addAllExtraLayers(map, yardMarkerSet);
                    extraLayers.push(yardMarkerSet);
                }
            }
            else if (!layers.yards && map.getLayer('yards')) {
                map.removeLayer('yards');
            }
            // Handles adding/removing the containers layer based on settings
            if (layers.containers && !map.getLayer('containers')) {
                if (map.getSource('containers')) {
                    await addExtraLayer(map, 'containers');
                }
                else {
                    const containerMarkerSet = await setupContainerMarkerSet();
                    await addAllExtraLayers(map, containerMarkerSet);
                    extraLayers.push(containerMarkerSet);
                }
            }
            else if (!layers.containers && map.getLayer('containers')) {
                map.removeLayer('containers');
            }
            // Setup event handlers for applicable extra layers
            const newEventHandlers = {};
            extraLayers.forEach((markerSet) => {
                newEventHandlers[markerSet.id] = addExtraLayerEventListeners(map, markerSet)[markerSet.id];
            });
            // Clean up old event listeners
            Object.entries(extraLayerEventHandlersRef.current).forEach(([markerLayerID, handlers]) => {
                const markerSet = markerSets.find((set) => set.id === markerLayerID);
                if (markerSet) {
                    removeExtraLayerEventListeners(map, markerSet, { [markerLayerID]: handlers });
                }
            });
            // Update the event handlers ref
            extraLayerEventHandlersRef.current = newEventHandlers;
        }
        catch (error) {
            console.error('Error initializing extra layers:', error);
        }
    };
    /**
     * Adds all layers to the map with the given marker set per extraLayer.
     *
     * @param map - The mapboxgl.Map instance.
     * @param markerSet - The marker set containing the markers and other properties.
     * @returns A Promise that resolves when all layers have been added.
     */
    const addAllExtraLayers = async (map, markerSet) => {
        try {
            if (map.hasImage(`layerIcon-${markerSet.id}`)) {
                map.removeImage(`layerIcon-${markerSet.id}`);
            }
            const layerIcon = await iconConverter({ icon: markerSet.icon, color: markerSet.color, size: 50 });
            map.addImage(`layerIcon-${markerSet.id}`, layerIcon);
            if (map.getLayer(`${markerSet.id}`)) {
                map.removeLayer(markerSet.id);
            }
            if (map.getSource(markerSet.id)) {
                map.removeSource(markerSet.id);
            }
            map.addSource(markerSet.id, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: markerSet.markers.map((markerData) => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: markerData.lngLat,
                        },
                        properties: {
                            id: markerData.id,
                            title: markerData.title,
                            label: markerData.label,
                            icon: `layerIcon-${markerSet.id}`,
                        },
                    })),
                },
            });
            await addExtraLayer(map, markerSet.id);
        }
        catch (error) {
            console.error('Error adding layer markers:', error);
        }
    };
    /**
     * Helper function to add just the layer per extraLayer given the desired ID (Assumes layerID is the same as the source ID).
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {string} layerID - The ID of the layer to be added.
     * @returns A Promise that resolves when the layer is added.
     */
    const addExtraLayer = async (map, layerID) => {
        map.addLayer({
            id: layerID,
            type: 'symbol',
            source: layerID,
            layout: {
                'icon-image': ['get', 'icon'], // Use the 'icon' property of each feature to determine the image
                'icon-size': 0.5, // Images are purposefully rasterized as very large to preserve detail, so we scale them down
                'icon-allow-overlap': true, // Removes effect where markers go invisible when zoomed out
            },
        });
    };
    /**
     * Helper function to add event listeners per extra layer and returns the event handlers.
     *
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MarkerSet} markerSet - The marker set associated with the extra layer.
     * @returns {ExtraLayerEventHandlers} - The event handlers for the extra layer.
     */
    const addExtraLayerEventListeners = useCallback((map, markerSet) => {
        const eventHandlers = {
            [markerSet.id]: {
                markerMouseEnter: (e) => {
                    map.getCanvas().style.cursor = 'pointer';
                    const features = map.queryRenderedFeatures(e.point);
                    const markerID = features?.[0]?.properties?.id;
                    const markerData = markerSet.markers.find((marker) => marker.id === markerID);
                    if (!markerData)
                        return;
                    const popupNode = document.createElement('div');
                    createRoot(popupNode).render(_jsx(MapPopup, { title: markerData.title, content: markerData.content }));
                    popupRef.current?.setLngLat(e.lngLat).setDOMContent(popupNode).addTo(map);
                },
                markerMouseLeave: () => {
                    if (!onDoubleClick)
                        map.getCanvas().style.cursor = '';
                    popupRef.current?.remove();
                },
            },
        };
        map.on('mouseenter', markerSet.id, eventHandlers[markerSet.id].markerMouseEnter);
        map.on('mouseleave', markerSet.id, eventHandlers[markerSet.id].markerMouseLeave);
        return eventHandlers;
    }, [layers]);
    /**
     * Helper function to remove event listeners per extra layer.
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MarkerSet} markerSet - The marker set associated with the extra layer.
     * @param {ExtraLayerEventHandlers} eventHandlers - The event handlers for the extra layer.
     */
    const removeExtraLayerEventListeners = useCallback((map, markerSet, eventHandlers) => {
        const markerLayerID = `${markerSet.id}`;
        map.off('mouseenter', markerLayerID, eventHandlers[markerLayerID].markerMouseEnter);
        map.off('mouseleave', markerLayerID, eventHandlers[markerLayerID].markerMouseLeave);
    }, []);
    /**
     * Helper function to add event listeners for the map and returns the event handlers.
     *
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @returns {MapEventHandlers} - The event handlers for the map.
     */
    const addMapEventListeners = (map) => {
        const eventHandlers = {
            moveStart: (e) => {
                if (e.originalEvent instanceof MouseEvent) {
                    setIsCentered(false);
                }
            },
            doubleClick: (e) => {
                if (onDoubleClick)
                    onDoubleClick({ latitude: e.lngLat.lat, longitude: e.lngLat.lng });
            },
        };
        map.on('movestart', eventHandlers.moveStart);
        map.on('dblclick', eventHandlers.doubleClick);
        return eventHandlers;
    };
    /**
     * Helper function to remove event listeners for the map.
     * @param {mapboxgl.Map} map - The Mapbox GL map instance.
     * @param {MapEventHandlers} eventHandlers - The event handlers for the map.
     */
    const removeMapEventListeners = useCallback((map, eventHandlers) => {
        map.off('moveStart', eventHandlers.moveStart);
        map.off('dblclick', eventHandlers.doubleClick);
    }, []);
    // Forces the map to reinitialize the layers and listeners when the markerSets change, requires the map to be fully loaded to do anything
    useEffect(() => {
        if (!map || !markerSets || !isMapLoaded) {
            return;
        }
        // if double click is enabled, change to cursor to be a pointer
        if (onDoubleClick)
            map.getCanvas().style.cursor = 'pointer';
        initLayersAndListeners(map);
        if (layers)
            initExtraLayers(map);
    }, [map, isMapLoaded, markerSets, settings, layers]);
    // Save settings to local storage whenever they change
    useEffect(() => {
        if (settings && !visibleSettings)
            localStorage.setItem('pin-map-settings', JSON.stringify(settings));
    }, [settings]);
    // Save layer controls to local storage whenever they change
    useEffect(() => {
        if (layers && !visibleExtraLayers)
            localStorage.setItem('pin-map-layers', JSON.stringify(layers));
        // if (map && layers) initExtraLayers(map);
    }, [map, layers]);
    return (_jsx("div", { className: "size-full", ref: mapRef, children: map && (_jsx(PinMapSettings, { map: map, settings: settings, setSettings: setSettings, layers: layers, setLayers: setLayers, isCentered: isCentered, onRecenter: showRecenter
                ? () => {
                    fitBounds(map, markerSets);
                    setIsCentered(true);
                }
                : undefined, disableRecenter: markerSets.length === 0 || markerSets.every((set) => set.markers.length === 0) })) }));
};
//# sourceMappingURL=PinMap.js.map